// import

import clsx from 'clsx';
import {styled} from 'goober';
import type {ComponentProps} from 'react';

import type {HomeMainNode} from 'libs/model';
import {Image} from 'modules/Image';
import {Link} from 'modules/Link';
import {Type} from 'modules/Type';

// types

type Props = ComponentProps<typeof Block> &
HomeMainNode['meta']['featuresRegion'];

// component

export function HomeFeatures(props: Props) {
  const {features, miniFeatures, ...rest} = props;

  return (
    <Block {...rest} data-test="features">
      <Type className="head">
        <h1>Why choose SiteArcade?</h1>
      </Type>

      <div className="minis">
        {miniFeatures.slice(0, 4).map(({headline, blurbMark, vectorImage}) => (
          <article key={headline} className="mini">
            {vectorImage ? (
              <figure>
                <Image
                  height={288}
                  layout="intrinsic"
                  src={vectorImage}
                  width={288}
                />
              </figure>
            ) : null}

            <header>{headline}</header>
            <Type className="blurb">{blurbMark}</Type>
          </article>
        ))}
      </div>

      {features.map((feat, key) => (
        <article key={key} className={clsx('feature', {rev: key % 2})}>
          <header>
            <h1>{feat.headline}</h1>
          </header>

          <div className="grid">
            <figure>
              <img alt={feat.headline} src={feat.vectorImage} />
            </figure>

            <Type className="content">
              <h2>{feat.subhead}</h2>

              <div
                className="body"
                dangerouslySetInnerHTML={{__html: feat.blurbMark}}
              />

              {feat.buttonLink && feat.buttonText ? (
                <nav>
                  <Link
                    className="button"
                    href={feat.buttonLink}
                    title={feat.buttonText}
                  >
                    {feat.buttonText}
                  </Link>
                </nav>
              ) : null}
            </Type>
          </div>
        </article>
      ))}
    </Block>
  );
}

// styles

const Block = styled('section')`
  padding: 4.5rem 1.5rem 9rem;
  position: relative;

  .head {
    margin: 0 auto;

    h1 {
      text-align: center;
      font-weight: 700;
    }
  }

  .minis {
    max-width: var(--bp-xl);
    margin: 9rem auto 12rem;
    padding: 0 1.5rem;
    display: grid;
    grid-template-columns: 1fr 1fr 1fr 1fr;
    gap: 6rem 3rem;
    justify-items: center;
  }

  .mini {
    max-width: var(--bp-xxs);
  }

  .mini figure {
    margin: 0 auto 0.5rem;
    max-width: 75%;
    * { display: block; }
  }

  .mini header {
    font-family: var(--font-head);
    letter-spacing: normal;
    font-weight: 700;
    text-align: center;
    font-size: 2rem;
    line-height: 2.25rem;
    margin-bottom: 0.5rem;
  }

  .mini .blurb {
    font-size: 1.125rem;
    line-height: 1.75rem;
    text-align: justify;
    p { hyphens: none; }
  }

  .feature {
    margin: 0 auto;
    max-width: var(--bp-l);

    &:nth-child(n + 2) {
      margin-top: 6rem;
    }

    header {
      position: relative;
      display: flex;
      align-content: flex-start;
      margin-bottom: 3rem;

      &::before {
        content: "";
        position: absolute;
        top: calc(50% - 3px);
        left: 0;
        width: 100%;
        display: block;
        border-radius: 100%;
        height: 6px;
        background:
          repeating-linear-gradient(
            -30deg,
            var(--yellow) 0,
            var(--yellow) 0.5rem,
            var(--red) 0.5rem,
            var(--red) 1.0rem,
            var(--blue) 1.0rem,
            var(--blue) 1.5rem
          );
      }

      h1 {
        flex: 0 1 auto;
        text-transform: uppercase;
        font-weight: 800;
        margin: 0;
        letter-spacing: normal;
        font-size: 3rem;
        line-height: 3rem;
        text-align: center;
        background-color: var(--white);
        position: relative;
        z-index: 1;
        padding-right: 0.5rem;
      }
    }
  }

  .grid {
    margin: 0 auto;
    max-width: var(--bp-ml);
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 3rem;

    figure {
      align-self: flex-start;
      flex: 0 1 18rem;
      margin: 0;

      img {
        width: 100%;
        height: auto;
        display: block;
      }
    }
  }

  .content {
    flex: 0 1 36rem;

    h2 {
      font-size: 1.75rem;
      margin-right: 0;
    }

    li:nth-child(n + 2) {
      margin-top: 1rem;
    }

    nav {
      margin-top: 1rem;
    }
  }

  .rev {
    .grid {
      flex-direction: row-reverse;
    }

    header {
      justify-content: flex-end;
    }

    h1 {
      padding-right: 0;
      padding-left: 0.5rem;
    }
  }

  @media (min-width: 54em) and (max-width: 62.99875em) {
    .minis { grid-template-columns: 1fr 1fr 1fr; }
    .mini:nth-child(n + 4) { display: none; }
  }

  @media (max-width: 53.99875em) {
    .minis { grid-template-columns: 1fr 1fr; }

    .feature {
      header {
        justify-content: center;
        flex-flow: column;
      }

      header::before {
        position: static;
        margin: 0 0 6rem;
      }
    }
  }

  @media (max-width: 35.99875em) {
    .minis { grid-template-columns: 1fr; }

    .feature {
      header h1 {
        font-size: 2.25rem;
        line-height: 2rem;
      }
    }

    .grid {
      display: block;

      figure {
        margin: 0 auto 3rem;
        max-width: 12rem;
      }
    }
  }
`;
